// src/points/PointsContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../auth/AuthContext';
import { StatsContext } from '../stats/StatsContext';
import axiosInstance from '../utils/RequestsQueue';
import { showSwalNotification } from '../utils/SwalResponse';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

export const PointsContext = createContext();

export default function PointsProvider({ children }) {
  const { authToken, currentUser } = useContext(AuthContext);
  const { fetchStats } = useContext(StatsContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [points, setPoints] = useState({
    totalEarned: 0,
    redeemable: 0,
    pending: 0,
    history: [],
    transactions: [],
  });
  const [prizes, setPrizes] = useState([]);
  const [pointsSummary, setPointsSummary] = useState({
    totalEarned: 0,
    totalRedeemable: 0,
    totalRedeemed: 0,
    totalPending: 0,
    totalUsers: 0,
    averagePoints: 0,
  });
  const [allHistory, setAllHistory] = useState([]);
  const [allUsersPoints, setAllUsersPoints] = useState([]);

  // ================== FETCH USER POINTS ================
  const fetchUserPoints = async () => {
    if (!authToken) {
      // Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      // navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/my-points`,
        method: "GET",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      if (response?.data?.success) {
        setPoints(response.data.data);
      } else {
        console.error('Failed to fetch points:', response?.data?.message);
      }
    } catch (error) {
      console.error('Error fetching points:', error?.response?.data);
      // return error?.response?.data;
    } finally {
      setLoading(false);
    }
  };

  // ================== FETCH USER HISTORY ================
  const fetchUserHistory = async (limit = 100) => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/my-history?limit=${limit}`,
        method: "GET",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      if (response?.data?.success) {
        setPoints(prev => ({ ...prev, history: response.data.data }));
      }
    } catch (error) {
      console.error('Error fetching history:', error?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // ================== FETCH AVAILABLE PRIZES ================
  const fetchPrizes = async () => {
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/prizes`,
        method: "GET",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      if (response?.data?.success) {
        setPrizes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching prizes:', error?.response?.data);
    }
  };

  // ================== REDEEM POINTS ================
  const redeemPoints = async (prizeId) => {
    if (!authToken) {
      Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/redeem`,
        method: "POST",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        data: { prize_id: prizeId }
      });
      showSwalNotification(response.data);
      if (response?.data?.success) {
        await fetchUserPoints();
        await fetchPrizes();
        fetchStats();
      }
      return response?.data;
    } catch (error) {
      console.error('Error redeeming points:', error?.response?.data);
      showSwalNotification(error?.response?.data || { success: false, message: 'Failed to redeem points' });
    } finally {
      setLoading(false);
    }
  };

  // ================== WITHDRAW CASH ================
  const withdrawCash = async (prizeId, method, phoneNumber) => {
    if (!authToken) {
      Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/withdraw-cash`,
        method: "POST",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        data: { 
          prize_id: prizeId, 
          method, 
          phone_number: phoneNumber 
        }
      });
      showSwalNotification(response.data);
      if (response?.data?.success) {
        await fetchUserPoints();
        await fetchPrizes();
        fetchStats();
      }
      return response?.data;
    } catch (error) {
      console.error('Error withdrawing cash:', error?.response?.data);
      showSwalNotification(error?.response?.data || { success: false, message: 'Failed to withdraw cash' });
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: GET ALL PRIZES ================
  const getAllPrizesAdmin = async () => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/prizes/all`,
        method: "GET",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      if (response?.data?.success) {
        setPrizes(response.data.data);
      }
      return response?.data;
    } catch (error) {
      console.error('Error fetching all prizes:', error?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: ADD PRIZE ================
  const addPrize = async (prizeData) => {
    if (!authToken) {
      Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/prizes`,
        method: "POST",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        data: prizeData
      });
      if (response?.data?.success) {
      showSwalNotification(response.data);
        await getAllPrizesAdmin();
      }
      return response?.data;
    } catch (error) {
      console.error('Error adding prize:', error?.response?.data);
      showSwalNotification(error?.response?.data || { success: false, message: 'Failed to add prize' });
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: UPDATE PRIZE ================
  const updatePrize = async (prizeId, prizeData) => {
    if (!authToken) {
      Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      // console.log(prizeId)
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/prizes/${prizeId}`,
        method: "PUT",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        data: prizeData
      });
      showSwalNotification(response.data);
      if (response?.data?.success) {
        await getAllPrizesAdmin();
      }
      return response?.data;
    } catch (error) {
      console.error('Error updating prize:', error?.response?.data);
      showSwalNotification(error?.response?.data || { success: false, message: 'Failed to update prize' });
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: DELETE PRIZE ================
  const deletePrize = async (prizeId) => {
    if (!authToken) {
      Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/prizes/${prizeId}`,
        method: "DELETE",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      showSwalNotification(response.data);
      if (response?.data?.success) {
        await getAllPrizesAdmin();
      }
      return response?.data;
    } catch (error) {
      console.error('Error deleting prize:', error?.response?.data);
      showSwalNotification(error?.response?.data || { success: false, message: 'Failed to delete prize' });
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: TOGGLE PRIZE STATUS ================
  const togglePrizeStatus = async (prizeId) => {
    if (!authToken) {
      Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/prizes/${prizeId}/toggle`,
        method: "POST",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      showSwalNotification(response.data);
      if (response?.data?.success) {
        await getAllPrizesAdmin();
      }
      return response?.data;
    } catch (error) {
      console.error('Error toggling prize status:', error?.response?.data);
      showSwalNotification(error?.response?.data || { success: false, message: 'Failed to toggle prize status' });
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: GET SYSTEM SUMMARY ================
  const getSystemSummary = async () => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/admin/summary`,
        method: "GET",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      if (response?.data?.success) {
        setPointsSummary(response.data.data);
      }
      return response?.data;
    } catch (error) {
      console.error('Error fetching system summary:', error?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: GET ALL HISTORY ================
  const getAllHistoryAdmin = async (limit = 100, offset = 0) => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/admin/history?limit=${limit}&offset=${offset}`,
        method: "GET",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      if (response?.data?.success) {
        setAllHistory(response.data.data);
      }
      return response?.data;
    } catch (error) {
      console.error('Error fetching all history:', error?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: GET ALL USERS POINTS ================
  const getAllUsersPoints = async () => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/admin/users`,
        method: "GET",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      if (response?.data?.success) {
        setAllUsersPoints(response.data.data);
      }
      return response?.data;
    } catch (error) {
      console.error('Error fetching all users points:', error?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: GET USER POINTS ================
  const getUserPointsAdmin = async (userId) => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/admin/user/${userId}/points`,
        method: "GET",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      return response?.data;
    } catch (error) {
      console.error('Error fetching user points:', error?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: ADJUST USER POINTS ================
  const adjustUserPoints = async (userId, points, description) => {
    if (!authToken) {
      Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/admin/user/${userId}/adjust`,
        method: "POST",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        data: { points, description }
      });
      showSwalNotification(response.data);
      if (response?.data?.success) {
        await getSystemSummary();
        await getAllUsersPoints();
      }
      return response?.data;
    } catch (error) {
      console.error('Error adjusting user points:', error?.response?.data);
      showSwalNotification(error?.response?.data || { success: false, message: 'Failed to adjust points' });
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: APPROVE PENDING POINTS ================
  const approvePendingPoints = async (transactionId) => {
    if (!authToken) {
      Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/pending/approve/${transactionId}`,
        method: "POST",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      showSwalNotification(response.data);
      if (response?.data?.success) {
        await fetchUserPoints();
        await getSystemSummary();
      }
      return response?.data;
    } catch (error) {
      console.error('Error approving pending points:', error?.response?.data);
      showSwalNotification(error?.response?.data || { success: false, message: 'Failed to approve points' });
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: REJECT PENDING POINTS ================
  const rejectPendingPoints = async (transactionId) => {
    if (!authToken) {
      Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/pending/reject/${transactionId}`,
        method: "POST",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      showSwalNotification(response.data);
      if (response?.data?.success) {
        await fetchUserPoints();
        await getSystemSummary();
      }
      return response?.data;
    } catch (error) {
      console.error('Error rejecting pending points:', error?.response?.data);
      showSwalNotification(error?.response?.data || { success: false, message: 'Failed to reject points' });
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: GET ALL WITHDRAWALS ================
  const getAllWithdrawals = async (status = null) => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    try {
      const url = status 
        ? `${process.env.REACT_APP_SERVER_URL}/api/points/admin/withdrawals?status=${status}`
        : `${process.env.REACT_APP_SERVER_URL}/api/points/admin/withdrawals`;
      const response = await axiosInstance.requestWithQueue({
        url: url,
        method: "GET",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      return response?.data;
    } catch (error) {
      console.error('Error fetching withdrawals:', error?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // ================== ADMIN: PROCESS WITHDRAWAL ================
  const processWithdrawal = async (withdrawalId, status, reference = null) => {
    if (!authToken) {
      Swal.fire("Info", "Your Session Has Expired. Please login again first", "info");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/admin/withdrawals/${withdrawalId}/process`,
        method: "POST",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        data: { status, reference }
      });
      showSwalNotification(response.data);
      return response?.data;
    } catch (error) {
      console.error('Error processing withdrawal:', error?.response?.data);
      showSwalNotification(error?.response?.data || { success: false, message: 'Failed to process withdrawal' });
    } finally {
      setLoading(false);
    }
  };

  // ================== GET USER WITHDRAWALS ================
  const getUserWithdrawals = async () => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.requestWithQueue({
        url: `${process.env.REACT_APP_SERVER_URL}/api/points/withdrawals`,
        method: "GET",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
      });
      return response?.data;
    } catch (error) {
      console.error('Error fetching withdrawals:', error?.response?.data);
    } finally {
      setLoading(false);
    }
  };
  // console.log(process.env.REACT_APP_SERVER_URL)

  // ================== INITIAL FETCH ================
  useEffect(() => {
    if (currentUser) {
      fetchUserPoints();
      fetchPrizes();
      // Admin functions will be called from admin components
    }
  }, [currentUser, authToken]);

  // Context data
  const contextData = {
    // State
    points,
    prizes,
    pointsSummary,
    allHistory,
    allUsersPoints,
    loading,
    
    // User functions
    fetchUserPoints,
    fetchUserHistory,
    fetchPrizes,
    redeemPoints,
    withdrawCash,
    getUserWithdrawals,
    
    // Admin functions
    getAllPrizesAdmin,
    addPrize,
    updatePrize,
    deletePrize,
    togglePrizeStatus,
    getSystemSummary,
    getAllHistoryAdmin,
    getAllUsersPoints,
    getUserPointsAdmin,
    adjustUserPoints,
    approvePendingPoints,
    rejectPendingPoints,
    getAllWithdrawals,
    processWithdrawal,
  };

  return (
    <PointsContext.Provider value={contextData}>
      {children}
    </PointsContext.Provider>
  );
}

// Custom hook for using points context
export const usePoints = () => {
  const context = useContext(PointsContext);
  if (!context) {
    throw new Error('usePoints must be used within a PointsProvider');
  }
  return context;
};

